/**
 * Labels for the Mode C order link page. English is the key. Hindi, Bengali and Tamil are first drafts
 * for the screen labels only and need a native speaker's review before launch (message templates are
 * separate and owner-supplied).
 */
export type LinkLang = "en" | "hi" | "bn" | "ta";

export const LANGS: { id: LinkLang; label: string }[] = [
  { id: "en", label: "EN" },
  { id: "hi", label: "हिं" },
  { id: "bn", label: "বাং" },
  { id: "ta", label: "தமி" },
];

const hi: Record<string, string> = {
  "Order from {designer}": "{designer} का ऑर्डर",
  Pieces: "पीस",
  "Due by": "देने की तारीख",
  "Rate per piece": "प्रति पीस रेट",
  Accept: "स्वीकार करें",
  Counter: "बदलाव भेजें",
  Decline: "मना करें",
  Update: "अपडेट भेजें",
  "Agreed terms": "तय शर्तें",
  "Waiting for {designer} to reply": "{designer} के जवाब का इंतज़ार",
  "Your rate per piece": "आपका प्रति पीस रेट",
  Quantity: "मात्रा",
  "Date you can deliver": "आप कब दे पाएंगे",
  "Send counter": "बदलाव भेजें",
  Cancel: "रद्द करें",
  Stage: "काम कहाँ तक पहुँचा",
  "Pieces done": "कितने पीस बने",
  Save: "सेव करें",
  "Add photos": "फ़ोटो जोड़ें",
  "Voice note": "आवाज़ संदेश",
  "Stop and send": "रोकें और भेजें",
  "Problem / delay": "समस्या / देरी",
  "What is the problem?": "क्या समस्या है?",
  Send: "भेजें",
  "Recent updates": "हाल के अपडेट",
  "Updates you send here are shared with {designer}.": "आप यहाँ जो भेजेंगे वह {designer} के साथ साझा होगा।",
  "This link was switched off by the designer.": "डिज़ाइनर ने यह लिंक बंद कर दिया है।",
  "This order is closed and its link has expired.": "यह ऑर्डर बंद हो गया है, लिंक की अवधि खत्म।",
  "This link doesn't work. Ask the designer for a new one.": "यह लिंक काम नहीं कर रहा। डिज़ाइनर से नया लिंक माँगें।",
  "Enter the code we sent to your phone": "आपके फ़ोन पर भेजा गया कोड डालें",
  "Send code": "कोड भेजें",
  Continue: "आगे बढ़ें",
  Sent: "भेज दिया",
  "Order accepted": "ऑर्डर स्वीकार",
  Accepted: "स्वीकार",
  Started: "शुरू",
  Dyeing: "रंगाई",
  Weaving: "बुनाई",
  Finishing: "फ़िनिशिंग",
  "On hold": "रुका हुआ",
  Stuck: "अटका",
  Completed: "पूरा",
  Declined: "मना किया",
  Cancelled: "रद्द",
};

const bn: Record<string, string> = {
  "Order from {designer}": "{designer}-এর অর্ডার",
  Pieces: "পিস",
  "Due by": "দেওয়ার তারিখ",
  "Rate per piece": "প্রতি পিস রেট",
  Accept: "গ্রহণ করুন",
  Counter: "পাল্টা প্রস্তাব",
  Decline: "না বলুন",
  Update: "আপডেট পাঠান",
  "Agreed terms": "ঠিক হওয়া শর্ত",
  "Waiting for {designer} to reply": "{designer}-এর উত্তরের অপেক্ষা",
  "Your rate per piece": "আপনার প্রতি পিস রেট",
  Quantity: "পরিমাণ",
  "Date you can deliver": "কবে দিতে পারবেন",
  "Send counter": "পাল্টা প্রস্তাব পাঠান",
  Cancel: "বাতিল",
  Stage: "কাজ কোথায়",
  "Pieces done": "কত পিস হয়েছে",
  Save: "সেভ করুন",
  "Add photos": "ছবি যোগ করুন",
  "Voice note": "ভয়েস নোট",
  "Stop and send": "থামিয়ে পাঠান",
  "Problem / delay": "সমস্যা / দেরি",
  "What is the problem?": "কী সমস্যা?",
  Send: "পাঠান",
  "Recent updates": "সাম্প্রতিক আপডেট",
  "Updates you send here are shared with {designer}.": "এখানে যা পাঠাবেন তা {designer}-এর সঙ্গে শেয়ার হবে।",
  "This link was switched off by the designer.": "ডিজাইনার এই লিংক বন্ধ করেছেন।",
  "This order is closed and its link has expired.": "অর্ডার বন্ধ, লিংকের মেয়াদ শেষ।",
  "This link doesn't work. Ask the designer for a new one.": "লিংক কাজ করছে না। ডিজাইনারের কাছে নতুন লিংক চান।",
  "Enter the code we sent to your phone": "ফোনে পাঠানো কোড দিন",
  "Send code": "কোড পাঠান",
  Continue: "এগিয়ে যান",
  Sent: "পাঠানো হয়েছে",
  "Order accepted": "অর্ডার গ্রহণ হয়েছে",
  Accepted: "গৃহীত",
  Started: "শুরু",
  Dyeing: "রং",
  Weaving: "বোনা",
  Finishing: "ফিনিশিং",
  "On hold": "স্থগিত",
  Stuck: "আটকে",
  Completed: "সম্পূর্ণ",
  Declined: "প্রত্যাখ্যাত",
  Cancelled: "বাতিল",
};

const ta: Record<string, string> = {
  "Order from {designer}": "{designer} ஆர்டர்",
  Pieces: "துண்டுகள்",
  "Due by": "கொடுக்க வேண்டிய நாள்",
  "Rate per piece": "ஒரு துண்டுக்கு விலை",
  Accept: "ஏற்கவும்",
  Counter: "மாற்று விலை",
  Decline: "மறுக்கவும்",
  Update: "அப்டேட் அனுப்பு",
  "Agreed terms": "ஒப்புக்கொண்ட நிபந்தனைகள்",
  "Waiting for {designer} to reply": "{designer} பதிலுக்காக காத்திருக்கிறது",
  "Your rate per piece": "உங்கள் விலை (ஒரு துண்டு)",
  Quantity: "அளவு",
  "Date you can deliver": "எப்போது கொடுக்க முடியும்",
  "Send counter": "மாற்று விலை அனுப்பு",
  Cancel: "ரத்து",
  Stage: "வேலை நிலை",
  "Pieces done": "முடிந்த துண்டுகள்",
  Save: "சேமி",
  "Add photos": "புகைப்படம் சேர்",
  "Voice note": "குரல் செய்தி",
  "Stop and send": "நிறுத்தி அனுப்பு",
  "Problem / delay": "பிரச்சனை / தாமதம்",
  "What is the problem?": "என்ன பிரச்சனை?",
  Send: "அனுப்பு",
  "Recent updates": "சமீபத்திய அப்டேட்கள்",
  "Updates you send here are shared with {designer}.": "இங்கே அனுப்புவது {designer} உடன் பகிரப்படும்.",
  "This link was switched off by the designer.": "டிசைனர் இந்த லிங்கை நிறுத்திவிட்டார்.",
  "This order is closed and its link has expired.": "ஆர்டர் முடிந்தது, லிங்க் காலாவதியானது.",
  "This link doesn't work. Ask the designer for a new one.": "லிங்க் வேலை செய்யவில்லை. புதிய லிங்க் கேளுங்கள்.",
  "Enter the code we sent to your phone": "உங்கள் போனுக்கு அனுப்பிய குறியீட்டை இடுங்கள்",
  "Send code": "குறியீடு அனுப்பு",
  Continue: "தொடரவும்",
  Sent: "அனுப்பப்பட்டது",
  "Order accepted": "ஆர்டர் ஏற்கப்பட்டது",
  Accepted: "ஏற்கப்பட்டது",
  Started: "தொடங்கியது",
  Dyeing: "சாயம்",
  Weaving: "நெசவு",
  Finishing: "இறுதி வேலை",
  "On hold": "நிறுத்தம்",
  Stuck: "சிக்கல்",
  Completed: "முடிந்தது",
  Declined: "மறுக்கப்பட்டது",
  Cancelled: "ரத்து",
};

const DICT: Record<LinkLang, Record<string, string>> = { en: {}, hi, bn, ta };

export const linkT = (lang: LinkLang, key: string, vars: Record<string, string> = {}): string => {
  let text = DICT[lang]?.[key] || key;
  Object.entries(vars).forEach(([k, v]) => {
    text = text.split(`{${k}}`).join(v);
  });
  return text;
};

export const asLang = (raw?: string | null): LinkLang =>
  raw === "hi" || raw === "bn" || raw === "ta" ? raw : "en";
